// =============================================================
// DamResetModal.jsx — empty the whole library, files included.
//
// This is the most destructive control in the module: it soft-deletes every
// asset row AND permanently deletes every byte in the `dam-assets` container.
// The dialog is therefore mostly COPY, deliberately — a small control attached
// to a very large lever, the SageExcludedAccountsPanel shape. What it has to
// get across is not "are you sure" but three specific facts a reader cannot
// infer from a button: the files are gone for good, the access logs are not,
// and share links / Claude connectors stop working rather than disappearing.
//
// IT NAMES REAL NUMBERS BEFORE IT ASKS. The preview counts each table and
// enumerates the container, so the confirmation reads "412 assets, 9
// collections, 1,204 files (18.3 GB)" — a decision somebody can actually make.
// It also shows assets and files SEPARATELY, because the gap between them is
// the orphaned bytes (a thumbnail, an abandoned upload, a dropbox copy whose
// row never landed) and that gap is the part a row count cannot see.
//
// THE RUN IS A LOOP OF BOUNDED LAPS, not one request. The server cannot delete
// thousands of blobs inside the ~45s Static Web Apps edge window, so it deletes
// a lap's worth and reports `done`. A lap that deleted nothing and failed at
// something comes back `stalled` and the loop STOPS with the reason shown
// verbatim — re-posting forever over a blob that cannot be deleted (an
// immutability policy, a legal hold) would spin for as long as the tab is open
// while looking like slow progress.
//
// Admin-only; the API is the real gate. Part of the removable DAM module.
// =============================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { Modal, ModalFooter, Btn, Input, Spinner, useToast } from '../../components/ui';
import { fetchResetPreview, runResetLap, formatBytes } from './damApi';

// A loop over a destructive server operation needs its own ceiling. At
// RESET_LAP_BLOBS (2,000) per lap this clears 100,000 files, and a run that
// somehow has not finished by then is not a run to keep feeding.
const MAX_LAPS = 50;

export default function DamResetModal({ onClose, onDone }) {
  const { toast } = useToast();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typed, setTyped] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);   // { laps, blobsDeleted, rows }
  const [error, setError] = useState(null);
  const [finished, setFinished] = useState(false);
  // A run must not keep posting deletes after the operator has closed the
  // dialog — every lap is irreversible, so an abandoned loop is worse here than
  // a stale render.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPreview(await fetchResetPreview()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const phrase = preview?.confirmPhrase || 'DELETE EVERYTHING';
  // Matched the way the API matches it (trim, collapse whitespace, upcase), so
  // the button never refuses a phrase the server would accept.
  const norm = (v) => String(v || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const armed = norm(typed) === norm(phrase);

  const run = async () => {
    setRunning(true); setError(null);
    const totals = { laps: 0, blobsDeleted: 0, rows: {} };
    try {
      for (let i = 0; i < MAX_LAPS; i++) {
        const res = await runResetLap(typed);
        if (!alive.current) return;
        totals.laps++;
        totals.blobsDeleted += res.blobsDeleted || 0;
        for (const [t, n] of Object.entries(res.rows || {})) totals.rows[t] = (totals.rows[t] || 0) + (n || 0);
        setProgress({ ...totals });

        if (res.stalled) {
          // Not a failure of the reset so far — the rows are gone and this
          // lap's files are gone. It is a specific, named thing left behind.
          setError(`${res.reason || 'Storage stopped accepting deletes'}${res.blobError ? ` — ${res.blobError}` : ''}`);
          break;
        }
        if (res.done) {
          setFinished(true);
          // Built from the run's ACCUMULATED totals, not the last lap's
          // res.summary — the row wipe happens on the first lap, so the final
          // lap's summary honestly reads "no rows left to delete" and would
          // render as the success message for a reset that cleared hundreds.
          toast.success(`${(totals.rows.DamAssets || 0).toLocaleString()} asset(s) deleted, `
            + `${totals.blobsDeleted.toLocaleString()} file(s) removed from storage`);
          break;
        }
        if (i === MAX_LAPS - 1) setError(`Stopped after ${MAX_LAPS} passes with files still in storage. Run it again to continue.`);
      }
      if (alive.current) { onDone && onDone(); }
    } catch (e) {
      // Shown verbatim: the API's refusal (a confirmation mismatch, an
      // uninitialized module) says more than anything invented here.
      if (alive.current) setError(e.message);
    } finally { if (alive.current) setRunning(false); }
  };

  const rows = preview?.rows || {};
  const tables = preview?.tables || [];
  const blobs = preview?.blobs;
  const nothingToDo = tables.every(t => !(rows[t.table] > 0)) && !(blobs?.count > 0);

  return (
    <Modal title="Delete everything in Digital Assets" onClose={onClose} width={620}>
      <div style={{ padding: space.xl, display: 'flex', flexDirection: 'column', gap: space.lg }}>

        {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: space.xl }}><Spinner /></div>}

        {!loading && preview?.needsInit && (
          <p style={{ margin: 0, fontSize: font.sm, color: B.textMd }}>
            This module has not been initialized, so there is nothing to delete.
          </p>
        )}

        {!loading && preview && !preview.needsInit && !finished && (
          <>
            {/* What goes. Counts first, because they are what makes this a
                decision rather than a leap. */}
            <div style={{ background: B.surfaceMuted, border: `1px solid ${B.border}`,
              borderRadius: radius.xl2, padding: space.lg }}>
              <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text, marginBottom: space.sm }}>
                This will delete
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: font.sm, color: B.textMd, lineHeight: 1.7 }}>
                {tables.map(t => (
                  <li key={t.table}>
                    <b>{(rows[t.table] || 0).toLocaleString()}</b> {t.label}
                    {t.mode === 'revoke' && <span style={{ color: B.textLt }}> — revoked, not removed (see below)</span>}
                  </li>
                ))}
                <li>
                  <b>{blobs ? `${(blobs.count || 0).toLocaleString()}${blobs.truncated ? '+' : ''}` : '—'}</b> file(s)
                  {blobs?.bytes ? ` (${formatBytes(blobs.bytes)}${blobs.truncated ? ' and counting' : ''})` : ''}
                  {' '}from storage — <b style={{ color: B.red }}>permanently</b>
                  {blobs?.truncated && <span style={{ color: B.textLt }}> — more than {blobs.count.toLocaleString()}, counted up to the cap</span>}
                </li>
              </ul>
              {preview.storageConfigured === false && (
                <div style={{ marginTop: space.sm, fontSize: font.xs, color: B.red }}>
                  Blob Storage is not configured on this deployment, so the rows will be deleted and no files will be touched.
                </div>
              )}
              {blobs?.error && (
                <div style={{ marginTop: space.sm, fontSize: font.xs, color: B.red }}>
                  Could not read the storage container: {blobs.error}
                </div>
              )}
            </div>

            {/* The three things a reader cannot infer from a button. */}
            <div style={{ fontSize: font.sm, color: B.textMd, lineHeight: 1.6, display: 'flex',
              flexDirection: 'column', gap: space.sm }}>
              <div>
                <b style={{ color: B.text }}>The files cannot be recovered.</b> Asset rows are soft-deleted the
                way everything else in Harvus is, but the bytes in the <code>{preview.container}</code> container
                are deleted outright. There is no undo and no recycle bin.
              </div>
              <div>
                <b style={{ color: B.text }}>Share links and Claude connectors are revoked, not removed.</b> They
                stay in their lists marked revoked, and anyone holding one gets a clear "this was turned off"
                rather than a broken page. Re-create them after you re-upload — a connector cannot be pointed at
                a library that no longer exists.
              </div>
              <div>
                <b style={{ color: B.text }}>Access logs are kept.</b> The record of who downloaded or searched
                what is about people, not assets, so it outlives the library.
              </div>
              {preview.exempt?.length > 0 && (
                <div style={{ fontSize: font.xs, color: B.textLt }}>
                  Kept: {preview.exempt.map(e => e.table).join(', ')}.
                </div>
              )}
              <div style={{ fontSize: font.xs, color: B.textLt }}>
                Files already imported from the large-file dropbox stay in its <b>Processed</b> folder, so nothing
                re-imports on its own. Move them back out of <b>Processed</b> if you want them ingested again.
              </div>
            </div>

            {nothingToDo ? (
              <p style={{ margin: 0, fontSize: font.sm, color: B.textMd }}>
                The library is already empty and no files are left in storage — there is nothing to delete.
              </p>
            ) : (
              <Input
                label={`Type ${phrase} to confirm`}
                value={typed}
                onChange={setTyped}
                placeholder={phrase}
                disabled={running}
              />
            )}
          </>
        )}

        {/* Progress. The bar never reads complete while laps are still running,
            and the file count is the one that matters — it is the irreversible
            half. */}
        {running && (
          <div style={{ display: 'flex', alignItems: 'center', gap: space.md, fontSize: font.sm, color: B.textMd,
            background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: radius.xl, padding: space.md }}>
            <span>
              Deleting… pass {progress?.laps || 1}
              {progress?.blobsDeleted ? `, ${progress.blobsDeleted.toLocaleString()} file(s) removed so far` : ''}
              . Keep this open until it finishes.
            </span>
          </div>
        )}

        {finished && (
          <div style={{ fontSize: font.sm, color: B.text, lineHeight: 1.6 }}>
            <b>Done.</b> {progress?.blobsDeleted?.toLocaleString() || 0} file(s) removed from storage
            {progress?.rows?.DamAssets ? `, ${progress.rows.DamAssets.toLocaleString()} asset(s) deleted` : ''}.
            The library is empty and ready to fill again.
          </div>
        )}

        {error && (
          <div style={{ background: B.red + '11', border: `1px solid ${B.red}`, color: B.red,
            borderRadius: radius.xl, padding: space.md, fontSize: font.sm, lineHeight: 1.5 }}>
            {error}
          </div>
        )}
      </div>

      <ModalFooter>
        <Btn variant="secondary" onClick={onClose} disabled={running}>
          {finished ? 'Close' : 'Cancel'}
        </Btn>
        {!finished && (
          <Btn onClick={run} disabled={!armed || running || nothingToDo || preview?.needsInit} loading={running}
            style={{ background: B.red, borderColor: B.red }}>
            Delete everything
          </Btn>
        )}
      </ModalFooter>
    </Modal>
  );
}
